import type { PixumEnv as PixumEnvType } from '../../types';
import { type Country, getCountryByLocale } from '../i18n';
import { type LocaleIso, getValidLocaleISO } from '../i18n/locale';
import { getTrustedShopId } from '../tracking/trustedshop';
import { getTrustPilotId, getTrustPilotSlimHeaderId, getTrustPilotTemplateId } from '../tracking/trustpilot';
import { getBaseUrl } from '../url/getBaseUrl';

/**
 * PixumEnv Class
 *
 * A singleton class that manages the global PIXUM_ENV object on the window.
 * Provides type-safe methods to initialize, read, and write to PIXUM_ENV.
 *
 * Features:
 * - Singleton pattern (only one instance exists)
 * - Type-safe get/set operations
 * - SSR-safe (handles missing window object)
 * - Initialization tracking
 * - Partial updates with automatic merging
 *
 * @example
 * ```typescript
 * // Initialize
 * PixumEnv.init(pixumEnvData);
 *
 * // Get a top-level property
 * const user = PixumEnv.get('USER');
 *
 * // Set a top-level property
 * PixumEnv.set('USER', { ...userData });
 *
 * // Update with merge
 * PixumEnv.update('TRACKING', { PREF: 'value' });
 *
 * // Get all data
 * const allData = PixumEnv.getAll();
 * ```
 */
class PixumEnvUtilsClass {
  private static instance: PixumEnvUtilsClass;
  private initialized = false;

  /**
   * Private constructor to enforce singleton pattern
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Get the singleton instance of PixumEnvUtilsClass
   *
   * @returns {PixumEnvUtilsClass} The singleton instance
   */
  static getInstance(): PixumEnvUtilsClass {
    if (!PixumEnvUtilsClass.instance) {
      PixumEnvUtilsClass.instance = new PixumEnvUtilsClass();
    }

    return PixumEnvUtilsClass.instance;
  }

  /**
   * Create a PIXUM_ENV object from country data
   *
   * This static factory method creates a new PixumEnvType object from Country data.
   * It initializes all required properties with default values.
   *
   * @param {Country} data - The country data to use for initialization
   * @param {LocaleIso} locale - The active locale; used to select the correct page for multi-language countries
   * @returns {PixumEnvType} A new PIXUM_ENV object
   *
   * @example
   * ```typescript
   * const pixumEnvData = PixumEnv.create(countryData, 'fr-BE');
   * PixumEnv.init(pixumEnvData);
   * ```
   */
  static create(data: Country, locale: LocaleIso): PixumEnvType {
    const { countryId, code: countryIso, pages, language, languageId, brandId } = data;
    // Use exact locale match so multi-language countries (e.g. BE with nl-BE and fr-BE)
    // resolve the correct page instead of always falling back to pages[0].
    const page = pages.find(p => p.locale === locale) ?? pages[0];
    const siteBranchId = page['siteBranch'];

    return {
      ASSET_HOST: '//assets.pixum.com',
      SERVICE_HOST: '/api/psi-service/',
      CDN_HOST: 'https://cdn.pixum.com',
      IMGIX_ASSET: 'https://pixum-assets.imgix.net/',
      IMGIX_CMS: '//pixum-cms.imgix.net/',
      LOCALE: page['locale'],
      LANGUAGE_ID: languageId,
      LANGUAGE_ISO: language,
      COUNTRY_ID: countryId,
      COUNTRY_ISO: countryIso,
      BRAND_ID: brandId,
      COUNTRY_URL: getBaseUrl() || page['homepage'],
      SITEBRANCH_ID: siteBranchId,
      CONTENTFUL_LOCALE: process.env['CONTENTFUL_LOCALE'],
      ENVIRONMENT: process.env['NODE_ENV'] || 'production',
      BRANCH_NAME: process.env[''],
      SESSION_ID: '',
      ASSET_STATIC_HOST: '',
      AUTH_BASE_URL: '',
      PAGE_TYPE: '',
      CMS: {
        BRANCH_ID: 0,
        LOCATION: '',
      },
      TRACKING: {
        PREF: '',
        GOOGLE_ANALYTICS_ID: '',
        TRUSTED_SHOP_ID: getTrustedShopId(siteBranchId),
        TRUST_PILOT_ID: getTrustPilotId(siteBranchId),
        TRUST_PILOT_TEMPLATE_ID: getTrustPilotTemplateId(),
        TRUST_PILOT_SLIM_HEADER_TEMPLATE_ID: getTrustPilotSlimHeaderId(),
      },
      USER: {
        firstName: '',
        lastName: '',
        email: '',
        birthday: '',
        sex: '',
        isAuthenticated: false,
        isNewCustomerSignup: false,
        jentisUserId: '',
        id: '',
        pref: '',
        voucher: '',
        prefInfo: {
          campaignId: '',
          pref: '',
          prefFull: '',
        },
      },
      UPLOAD: {},
      I18N: {},
      PRICE_FORMAT: {
        formatString: '',
        separators: {
          decimal: '',
          thousands: '',
        },
      },
      MINI_CART: {
        cartLocation: '',
        ot: '',
        totalAddonArticleCount: 0,
        totalArticleCount: 0,
        totalArticleSumGross: 0,
        totalArticleSumNet: 0,
        totalMainArticleCount: 0,
        totalPhotoArticleCount: 0,
        totalPositionCount: 0,
      },
      AUTH_PROPS: {
        I18N: {
          authModule: {},
        },
      },
      JENTIS: {
        page: {
          category: '',
          entity: '',
          language: '',
          name: '',
          type: '',
        },
      },
    };
  }

  /**
   * Generate an initialization script string for PIXUM_ENV
   *
   * This static method creates a JavaScript string that initializes window.PIXUM_ENV
   * with data generated from Country information. The returned string is meant to be
   * injected as inline script content (e.g., in a Next.js Script component).
   *
   * Accepts two call signatures:
   * 1. Legacy: `getInitScript(locale)` — resolves Country from locale (backward compatible)
   * 2. RSC: `getInitScript(data, pixumUser)` — accepts pre-resolved Country + optional user data
   *    (used by async PixumEnvScript RSC which fetches session/user data server-side)
   *
   * @param {LocaleIso | Country} localeOrData - Locale string or pre-resolved Country object
   * @param {Partial<PixumEnvType['USER']>} [pixumUser] - Optional user data to merge into USER field
   * @returns {string} JavaScript code string that assigns PIXUM_ENV to window
   *
   * @example Legacy usage
   * ```typescript
   * const scriptContent = PixumEnvUtils.getInitScript('fr-BE');
   * ```
   *
   * @example RSC usage with server-fetched user data
   * ```typescript
   * const scriptContent = PixumEnvUtils.getInitScript(countryData, {
   *   firstName: 'Jane',
   *   isAuthenticated: true,
   * });
   * ```
   */
  static getInitScript(localeOrData: LocaleIso | Country, pixumUser?: Partial<PixumEnvType['USER']>): string {
    let data: Country;
    let locale: LocaleIso;

    if (typeof localeOrData === 'string') {
      // Legacy call: derive country from locale string
      locale = localeOrData;
      data = getCountryByLocale(locale);
    } else {
      // RSC call: country already resolved; derive locale from env (same as ServerFetchTransformer)
      data = localeOrData;
      locale = getValidLocaleISO(process.env['CONTENTFUL_LOCALE']);
    }

    const pixumEnvData = PixumEnvUtilsClass.create(data, locale);

    if (pixumUser) {
      pixumEnvData.USER = { ...pixumEnvData.USER, ...pixumUser };
    }

    return `window.PIXUM_ENV = ${JSON.stringify(pixumEnvData)}`;
  }

  /**
   * Get the window object safely (handles SSR)
   *
   * @private
   * @returns {Window | undefined} The window object or undefined if not in browser
   */
  private getWindow(): Window | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return window;
  }

  /**
   * Initialize PIXUM_ENV on the window object
   *
   * @param {PixumEnvType} data - The initial PIXUM_ENV data
   * @throws {Error} If called in SSR environment
   *
   * @example
   * ```typescript
   * PixumEnv.init(getPixumEnv(countryData));
   * ```
   */
  init(data: PixumEnvType): void {
    const win = this.getWindow();

    if (!win) {
      console.warn('PixumEnv.init() called in SSR environment. Skipping initialization.');

      return;
    }

    win.PIXUM_ENV = data;
    this.initialized = true;
  }

  /**
   * Check if PIXUM_ENV is initialized
   *
   * @returns {boolean} True if initialized, false otherwise
   *
   * @example
   * ```typescript
   * if (PixumEnv.isInitialized()) {
   *   // Safe to use PixumEnv
   * }
   * ```
   */
  isInitialized(): boolean {
    const win = this.getWindow();

    return this.initialized && !!win?.PIXUM_ENV;
  }

  /**
   * Ensure PIXUM_ENV is initialized before access. If not initialized, attempts to initialize from window.PIXUM_ENV.
   *
   * @private
   */
  private ensureInitialized(): void {
    if (!this.isInitialized() && window?.PIXUM_ENV) {
      this.init(window.PIXUM_ENV as PixumEnvType);
    }
  }

  /**
   * Get a top-level property from PIXUM_ENV
   *
   * @template K - Key of PixumEnvType
   * @param {K} key - The top-level key to retrieve
   * @returns {PixumEnvType[K] | undefined} The value or undefined if not found or not initialized
   *
   * @example
   * ```typescript
   * const user = PixumEnv.get('USER');
   * const tracking = PixumEnv.get('TRACKING');
   * ```
   */
  get<K extends keyof PixumEnvType>(key: K): PixumEnvType[K] | undefined {
    const win = this.getWindow();

    if (!win || !win.PIXUM_ENV) {
      return undefined;
    }

    const pixumEnv = win.PIXUM_ENV as PixumEnvType;

    return pixumEnv[key];
  }

  /**
   * Set a top-level property in PIXUM_ENV (replaces entire value)
   *
   * @template K - Key of PixumEnvType
   * @param {K} key - The top-level key to set
   * @param {PixumEnvType[K]} value - The value to set
   * @throws {Error} If PIXUM_ENV is not initialized
   *
   * @example
   * ```typescript
   * PixumEnv.set('USER', {
   *   firstName: 'John',
   *   lastName: 'Doe',
   *   email: 'john@example.com',
   *   isAuthenticated: true,
   * });
   * ```
   */
  set<K extends keyof PixumEnvType>(key: K, value: PixumEnvType[K]): void {
    this.ensureInitialized();
    const win = this.getWindow();

    if (!win || !win.PIXUM_ENV) {
      return;
    }

    const pixumEnv = win.PIXUM_ENV as PixumEnvType;

    pixumEnv[key] = value;
  }

  /**
   * Update a top-level property in PIXUM_ENV (merges with existing value)
   *
   * For object types, performs a shallow merge. For primitive types, replaces the value.
   *
   * @template K - Key of PixumEnvType
   * @param {K} key - The top-level key to update
   * @param {Partial<PixumEnvType[K]>} value - The partial value to merge
   * @throws {Error} If PIXUM_ENV is not initialized
   *
   * @example
   * ```typescript
   * // Merge tracking data
   * PixumEnv.update('TRACKING', { PREF: 'new-value' });
   *
   * // Merge user data
   * PixumEnv.update('USER', { isAuthenticated: true });
   * ```
   */
  update<K extends keyof PixumEnvType>(key: K, value: Partial<PixumEnvType[K]>): void {
    this.ensureInitialized();
    const win = this.getWindow();

    if (!win || !win.PIXUM_ENV) {
      return;
    }

    const pixumEnv = win.PIXUM_ENV as PixumEnvType;
    const currentValue = pixumEnv[key];

    // Deep merge for objects
    if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
      pixumEnv[key] = { ...currentValue, ...value } as PixumEnvType[K];
    } else {
      // For primitives or arrays, replace the value
      pixumEnv[key] = value as PixumEnvType[K];
    }
  }

  /**
   * Get the entire PIXUM_ENV object
   *
   * @returns {PixumEnvType | undefined} The entire PIXUM_ENV object or undefined if not initialized
   *
   * @example
   * ```typescript
   * const allData = PixumEnv.getAll();
   * console.log(allData?.LOCALE);
   * ```
   */
  getAll(): PixumEnvType | undefined {
    const win = this.getWindow();

    return win?.PIXUM_ENV as PixumEnvType | undefined;
  }

  /**
   * Reset the initialization state (useful for testing)
   *
   * @internal
   */
  reset(): void {
    this.initialized = false;
    this.extended = false;
    this.extendedListeners = [];
    this.persistentExtendedListeners = [];
    const win = this.getWindow();

    if (win) {
      // @ts-expect-error - Resetting for testing purposes
      win.PIXUM_ENV = undefined;
    }
  }

  private extendedListeners: Array<() => void> = [];
  private persistentExtendedListeners: Array<() => void> = [];
  private extended = false;

  /**
   * Called by PixumEnvExtensionApplier after it updates PIXUM_ENV.USER with session data.
   * Triggers all registered onceExtended callbacks and marks the env as extended.
   */
  notifyExtended(): void {
    this.extended = true;
    const listeners = this.extendedListeners;
    this.extendedListeners = [];
    listeners.forEach(cb => cb());
    this.persistentExtendedListeners.forEach(cb => cb());
  }

  /**
   * Registers a one-time callback that fires when PIXUM_ENV has been extended with session
   * data. If the env is already extended, the callback is invoked synchronously.
   *
   * Returns a cleanup function that unregisters the callback (for use in useEffect returns).
   */
  onceExtended(callback: () => void): () => void {
    if (this.extended) {
      callback();

      return () => undefined;
    }

    this.extendedListeners.push(callback);

    return () => {
      this.extendedListeners = this.extendedListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Registers a persistent callback that fires on every notifyExtended() call.
   * Unlike onceExtended, this callback is NOT drained after the first invocation —
   * it continues to fire on every subsequent extension (e.g. after login/logout).
   *
   * Returns a cleanup function that unregisters the callback (for use in useEffect returns).
   */
  subscribeExtended(callback: () => void): () => void {
    this.persistentExtendedListeners.push(callback);

    return () => {
      this.persistentExtendedListeners = this.persistentExtendedListeners.filter(cb => cb !== callback);
    };
  }
}

/**
 * Singleton instance of PixumEnvUtilsClass with static method access
 *
 * This object provides access to both instance methods (init, get, set, etc.)
 * and static factory methods (create, getInitScript).
 *
 * @example
 * ```typescript
 * import { PixumEnvUtils } from 'utils';
 *
 * // Use static create method
 * const data = PixumEnvUtils.create(countryData);
 *
 * // Use static getInitScript method
 * const script = PixumEnvUtils.getInitScript(countryData);
 *
 * // Use instance methods
 * PixumEnvUtils.init(data);
 * const user = PixumEnvUtils.get('USER');
 * ```
 */
const instance = PixumEnvUtilsClass.getInstance();

export const PixumEnvUtils = Object.assign(instance, {
  create: PixumEnvUtilsClass.create.bind(PixumEnvUtilsClass),
  getInitScript: PixumEnvUtilsClass.getInitScript.bind(PixumEnvUtilsClass),
});
